import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError, downloadBlob } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/ui-helpers";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash, FileArrowDown, ShieldCheck, Warning, DownloadSimple } from "@phosphor-icons/react";

function Stat({ label, value, danger, testid }) {
  return (
    <div className="border border-border bg-card p-4">
      <div className="overline text-muted-foreground text-xs">{label}</div>
      <div data-testid={testid} className={`text-2xl font-bold mt-1 ${danger && value > 0 ? "text-red-600" : ""}`}>{value}</div>
    </div>
  );
}

export default function Cip007Ports() {
  const { isRole } = useAuth();
  const canEdit = isRole("admin", "analyst");
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [assets, setAssets] = useState([]);
  const [modal, setModal] = useState(null);
  const [denied, setDenied] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [su, li] = await Promise.all([api.get("/cip007/r1/summary"), api.get("/cip007/r1/ports")]);
      setSummary(su.data); setRecords(li.data.records); setAssets(li.data.assets); setDenied(false);
    } catch (e) { if (e.response?.status === 403) setDenied(true); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const doExport = async () => {
    try { await downloadBlob("/cip007/r1-export", "CIP007_R1_Ports_Services.docx"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const documentedIds = new Set(records.map((r) => r.asset_id));
  const undocumented = assets.filter((a) => !documentedIds.has(a.id));
  const openEditor = async (assetId) => {
    try { const { data } = await api.get(`/cip007/r1/ports/${assetId}`); setModal({ assetId, data }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (assetId) => { try { await api.delete(`/cip007/r1/ports/${assetId}`); loadAll(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div data-testid="cip007-ports-page">
      <PageHeader title="CIP-007-6 R1 — Ports & Services"
        subtitle="R1.1 logical network accessible ports (needed + justified) · R1.2 physical I/O port protection"
        action={<Button data-testid="c7r1-export-btn" className="rounded-none bg-primary hover:bg-primary/90" onClick={doExport}><FileArrowDown size={15} className="mr-1" />R1 Evidence</Button>} />
      <div className="p-8 space-y-6">
        {denied && (
          <div data-testid="c7r1-bcsi-denied" className="flex items-start gap-2 border border-amber-300 bg-amber-50 px-4 py-3 text-sm">
            <Warning size={18} className="mt-0.5 shrink-0 text-amber-600" />
            <p className="text-amber-800">BCSI access required. This module handles BES Cyber System Information — ask an administrator to grant you BCSI access to view and edit CIP-007 R1 registers.</p>
          </div>
        )}
        {summary && (
          <div data-testid="c7r1-summary" className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Assets documented" value={`${summary.assets_documented} / ${summary.total_assets}`} testid="c7r1-stat-documented" />
            <Stat label="Unauthorized ports" value={summary.unauthorized_ports} danger testid="c7r1-stat-unauthorized" />
            <Stat label="Physical protection gaps" value={summary.physical_protection_gaps} danger testid="c7r1-stat-physical" />
            <Stat label="Total Cyber Assets" value={summary.total_assets} testid="c7r1-stat-total" />
          </div>
        )}

        <div className="border border-border bg-card">
          <div className="border-b border-border px-4 py-3 flex items-center justify-between">
            <div><div className="font-display font-bold text-sm">Documented port registers (R1.1 / R1.2)</div></div>
            {canEdit && undocumented.length > 0 && (
              <Select onValueChange={(v) => openEditor(v)}>
                <SelectTrigger data-testid="c7r1-add-asset" className="rounded-none h-9 w-64 text-xs"><SelectValue placeholder="+ Document an asset…" /></SelectTrigger>
                <SelectContent>{undocumented.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border bg-secondary/40 text-left overline text-muted-foreground">
              <th className="px-4 py-2">Cyber Asset</th><th className="px-4 py-2">Needed Ports</th><th className="px-4 py-2">Unauthorized</th><th className="px-4 py-2">Physical (R1.2)</th><th className="px-4 py-2">Reviewed</th><th className="px-4 py-2"></th>
            </tr></thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.asset_id} data-testid={`c7r1-row-${r.asset_id}`} className="border-b border-border">
                  <td className="px-4 py-2 font-medium">{r.asset_name}</td>
                  <td className="px-4 py-2">{r.needed_count}</td>
                  <td className="px-4 py-2">{r.unauthorized_count > 0 ? <span className="text-red-600 font-semibold">{r.unauthorized_count}</span> : "0"}</td>
                  <td className="px-4 py-2">{r.physical_ports_protected ? <span className="text-green-700">Protected</span> : <span className="text-red-600 font-semibold">Gap</span>}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.review_date || "—"}</td>
                  <td className="px-4 py-2 flex gap-2">
                    {canEdit && <button data-testid={`c7r1-edit-${r.asset_id}`} onClick={() => openEditor(r.asset_id)} className="text-xs underline text-muted-foreground hover:text-foreground">edit</button>}
                    {canEdit && <button data-testid={`c7r1-del-${r.asset_id}`} onClick={() => del(r.asset_id)} className="text-muted-foreground hover:text-red-600"><Trash size={15} /></button>}
                  </td>
                </tr>
              ))}
              {records.length === 0 && <tr><td colSpan={6} className="px-4 py-4 text-muted-foreground text-sm">No port registers documented. Use "+ Document an asset" to start.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {modal && <PortEditor modal={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); loadAll(); }} />}
    </div>
  );
}

function PortEditor({ modal, onClose, onSaved }) {
  const d = modal.data || {};
  const [ports, setPorts] = useState(d.ports || []);
  const [physProt, setPhysProt] = useState(!!d.physical_ports_protected);
  const [physMethod, setPhysMethod] = useState(d.physical_protection_method || "");
  const [reviewer, setReviewer] = useState(d.reviewer || "");
  const [notes, setNotes] = useState(d.notes || "");

  const setPort = (i, k, v) => setPorts((p) => p.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const addPort = () => setPorts((p) => [...p, { protocol: "TCP", port_number: "", service: "", justification: "", needed: true, tfe: false }]);
  const rmPort = (i) => setPorts((p) => p.filter((_, idx) => idx !== i));

  const seed = async () => {
    try {
      const { data } = await api.post(`/cip007/r1/ports/${modal.assetId}/seed-from-baseline`);
      setPorts((prev) => {
        const seen = new Set(prev.map((p) => `${p.protocol}:${p.port_number}`));
        const merged = [...prev];
        data.seeded_ports.forEach((sp) => { if (!seen.has(`${sp.protocol}:${sp.port_number}`)) merged.push(sp); });
        return merged;
      });
      toast.success(`Seeded ${data.count} port(s) from latest baseline`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    const clean = ports
      .filter((p) => String(p.port_number).trim() !== "")
      .map((p) => ({ ...p, port_number: parseInt(p.port_number, 10) || 0 }));
    try {
      await api.put(`/cip007/r1/ports/${modal.assetId}`, {
        asset_id: modal.assetId, ports: clean, physical_ports_protected: physProt,
        physical_protection_method: physMethod, reviewer, notes,
      });
      toast.success("R1 register saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck size={18} />R1 Ports & Services — {d.asset_name || modal.assetId}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm">R1.1 — Logical network accessible ports</Label>
            <div className="flex gap-2">
              <Button data-testid="c7r1-seed-btn" size="sm" variant="outline" className="rounded-none" onClick={seed}><DownloadSimple size={13} className="mr-1" />Seed from baseline</Button>
              <Button data-testid="c7r1-addport-btn" size="sm" variant="outline" className="rounded-none" onClick={addPort}><Plus size={13} className="mr-1" />Add port</Button>
            </div>
          </div>
          <div className="border border-border max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-border bg-secondary/40 text-left overline text-muted-foreground">
                <th className="px-2 py-1">Proto</th><th className="px-2 py-1">Port</th><th className="px-2 py-1">Service</th><th className="px-2 py-1">Justification (R1.1)</th><th className="px-2 py-1">Needed</th><th className="px-2 py-1">TFE</th><th className="px-2 py-1"></th>
              </tr></thead>
              <tbody>
                {ports.map((p, i) => (
                  <tr key={i} data-testid={`c7r1-port-${i}`} className="border-b border-border">
                    <td className="px-2 py-1">
                      <Select value={p.protocol} onValueChange={(v) => setPort(i, "protocol", v)}>
                        <SelectTrigger className="rounded-none h-7 w-16 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="TCP">TCP</SelectItem><SelectItem value="UDP">UDP</SelectItem></SelectContent>
                      </Select>
                    </td>
                    <td className="px-2 py-1"><Input data-testid={`c7r1-port-num-${i}`} value={p.port_number} onChange={(e) => setPort(i, "port_number", e.target.value)} className="rounded-none h-7 w-20 text-xs" /></td>
                    <td className="px-2 py-1"><Input value={p.service} onChange={(e) => setPort(i, "service", e.target.value)} className="rounded-none h-7 text-xs" /></td>
                    <td className="px-2 py-1"><Input data-testid={`c7r1-port-just-${i}`} value={p.justification} onChange={(e) => setPort(i, "justification", e.target.value)} className="rounded-none h-7 text-xs" placeholder="why needed" /></td>
                    <td className="px-2 py-1 text-center"><Checkbox data-testid={`c7r1-port-needed-${i}`} checked={p.needed} onCheckedChange={(v) => setPort(i, "needed", !!v)} className="rounded-none" /></td>
                    <td className="px-2 py-1 text-center"><Checkbox checked={p.tfe} onCheckedChange={(v) => setPort(i, "tfe", !!v)} className="rounded-none" /></td>
                    <td className="px-2 py-1"><button onClick={() => rmPort(i)} className="text-muted-foreground hover:text-red-600"><Trash size={13} /></button></td>
                  </tr>
                ))}
                {ports.length === 0 && <tr><td colSpan={7} className="px-2 py-3 text-muted-foreground">No ports. Seed from a CIP-010 baseline or add manually.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex items-start gap-2 border-t border-border pt-3">
            <Warning size={16} className="mt-1 text-amber-600" />
            <p className="text-xs text-muted-foreground">Ports marked <b>not needed</b> (and not covered by a TFE) count as unauthorized and should be disabled per R1.1.</p>
          </div>
          <div className="border-t border-border pt-3 space-y-2">
            <Label className="text-sm">R1.2 — Physical I/O port protection</Label>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox data-testid="c7r1-physical" checked={physProt} onCheckedChange={(v) => setPhysProt(!!v)} className="rounded-none" />Unnecessary physical I/O ports (network, console, removable media) are protected</label>
            <Input data-testid="c7r1-physical-method" value={physMethod} onChange={(e) => setPhysMethod(e.target.value)} placeholder="Protection method (e.g. port blockers, disabled USB, physical access controls)" className="rounded-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Reviewer</Label><Input data-testid="c7r1-reviewer" value={reviewer} onChange={(e) => setReviewer(e.target.value)} className="rounded-none mt-1" /></div>
            <div><Label>Notes</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-none mt-1" /></div>
          </div>
          <Button data-testid="c7r1-save-btn" onClick={save} className="w-full rounded-none bg-primary hover:bg-primary/90">Save R1 Register</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
